import React, { useCallback, useEffect, useState } from "react";
import { salaryEnums } from "../../../Constant/HR/staffSalary";
import BaseInput from "../../../BaseComponents/BaseInput";
import { PlaceHolderFormat } from "../../../Constant/requireMessage";
import { SelectPlaceHolder, validationMessages } from "../../../Constant/validation";
import BaseSelect from "../../../BaseComponents/BaseSelect";
import { Submit } from "../../../Constant";
import BaseButton from "../../../BaseComponents/BaseButton";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess, handleEditClick, RupeesSymbol } from "../../../Constant/common";
import { effectiveCycleApi, reviseSalaryApi, viewSalaryStructureApi } from "../../../Api/salaryApi";
import { salaryStructureTitle } from "../../../Constant/title";
import { usePageLoader } from "../../../_helper/loading/LoadingContext";
import BackButton from "../../../BaseComponents/BackButton";
import { DollarSign, TrendingUp } from "react-feather";

const ReviseSalary = () => {
  document.title = salaryStructureTitle;
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = usePageLoader();
  const [btnLoader, setBtnLoader] = useState(false);
  const [monthlyCTC, setMonthlyCTC] = useState(null);
  const [yearlyCTC, setYearlyCTC] = useState(null);
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [previousSalary, setPreviousSalary] = useState(null);
  const [effectiveCycle, setEffectiveCycle] = useState(null);
  const [currentEffectiveCycle, setCurrentEffectiveCycle] = useState(null);
  const [useMonthlyAmount, setUseMonthlyAmount] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([effectiveCycleApi(staffId), viewSalaryStructureApi(staffId)])
      .then(([cycleResp, structureResp]) => {
        if (checkStatusCodeSuccess(cycleResp.statusCode)) {
          setEffectiveCycle(
            cycleResp?.data?.map((item) => ({ value: item, label: item, id: item })),
          );
          setCurrentEffectiveCycle(cycleResp?.data?.[0]);
        } else toast.error(cycleResp?.message);
        if (checkStatusCodeSuccess(structureResp.statusCode)) {
          setSalaryStructure(structureResp?.data);
          setPreviousSalary(structureResp?.data?.monthly_salary);
        } else toast.error(structureResp?.message);
      })
      .catch((err) => toast.error(err?.response?.data?.message ?? err?.message))
      .finally(() => setLoading(false));
  }, [staffId, setLoading]);

  useEffect(() => {
    fetchData();
    handleEditClick();
  }, [fetchData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      current_ctc: salaryStructure?.monthly_salary,
      revise_percentage: null,
      monthly_amount: salaryStructure?.monthly_salary,
      allowance_monthly: salaryStructure?.monthly_special_allowance,
      allowance_yearly: salaryStructure?.yearly_special_allowance,
      basic_monthly: salaryStructure?.monthly_salary,
      basic_yearly: salaryStructure?.yearly_salary,
      effective_cycle: currentEffectiveCycle,
    },
    validationSchema: yup.object({
      revise_percentage: yup
        .string()
        .required(validationMessages.required(salaryEnums.REVISE_BY_PERCENTAGE)),
      monthly_amount: yup
        .string()
        .required(validationMessages.required(salaryEnums.MONTHLY_AMOUNT)),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      reviseSalaryApi(staffId, {
        revised_percentage: parseFloat(Number(values.revise_percentage).toFixed(2)),
        previous_salary: previousSalary,
        revised_monthly_salary: parseFloat(Number(values.basic_monthly).toFixed(2)),
        revised_yearly_salary: parseFloat(Number(values.basic_yearly).toFixed(2)),
        effective_cycle: values.effective_cycle,
      })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            formik.resetForm();
            navigate(`/salaryStructure/${staffId}`);
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message ?? err?.message))
        .finally(() => setBtnLoader(false));
    },
  });

  useEffect(() => {
    setMonthlyCTC(Number(formik.values.basic_monthly) + Number(formik.values.allowance_monthly));
    setYearlyCTC(Number(formik.values.basic_yearly) + Number(formik.values.allowance_yearly));
  }, [
    formik.values.basic_monthly,
    formik.values.allowance_monthly,
    formik.values.basic_yearly,
    formik.values.allowance_yearly,
  ]);

  const handleBasicMonthlyChange = (e) => {
    const v = Number(e.target.value);
    const ctc = Number(formik.values.current_ctc);
    formik.setFieldValue("monthly_amount", v);
    formik.setFieldValue("basic_monthly", v);
    formik.setFieldValue("basic_yearly", v * 12);
    formik.setFieldValue("revise_percentage", ((v - ctc) / ctc) * 100);
  };

  const handlePercentageChange = (e) => {
    const v = e.target.value;
    const ctc = Number(formik.values.current_ctc);
    const newSalary = ctc + (v * ctc) / 100;
    formik.setFieldValue("revise_percentage", v);
    formik.setFieldValue("monthly_amount", newSalary);
    formik.setFieldValue("basic_monthly", newSalary);
    formik.setFieldValue("basic_yearly", newSalary * 12);
  };

  const pctChange = Number(formik.values.revise_percentage) || 0;
  const pctColor = pctChange > 0 ? "#10b981" : pctChange < 0 ? "#f43f5e" : "var(--text-muted)";

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {salaryEnums.REVISE_SALARY}
          </h1>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            Revise salary by percentage or fixed monthly amount
          </p>
        </div>
        <BackButton onClick={() => navigate(`/salaryStructure/${staffId}`)} />
      </div>

      <form
        onSubmit={formik.handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        {/* Config card */}
        <div
          className="animate-slide-up"
          style={{
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          <div style={{ height: 3, background: "linear-gradient(90deg,#10b981,#06b6d4)" }} />
          <div style={{ padding: "20px 24px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 16,
              }}
            >
              <BaseInput
                label={salaryEnums.STAFF_TYPE}
                name="staff_type"
                type="text"
                disabled
                value={salaryEnums.REGULAR}
                handleChange={() => {}}
                handleBlur={() => {}}
              />
              <BaseInput
                label={salaryEnums.WAGE_RATE}
                name="wage_rate"
                type="text"
                disabled
                value={salaryEnums.MONTHLY}
                handleChange={() => {}}
                handleBlur={() => {}}
              />
              <BaseSelect
                name="effective_cycle"
                label={salaryEnums.EFFECTIVE_CYCLE}
                options={effectiveCycle}
                placeholder={SelectPlaceHolder(salaryEnums.EFFECTIVE_CYCLE)}
                handleChange={(field, value) => formik.setFieldValue(field, value)}
                handleBlur={() => formik.setFieldTouched(salaryEnums.EFFECTIVE_CYCLE, true)}
                value={formik.values.effective_cycle}
                touched={formik.touched.effective_cycle}
                error={formik.errors.effective_cycle}
              />
            </div>
          </div>
        </div>

        {/* Revision inputs */}
        <div
          className="animate-slide-up"
          style={{
            animationDelay: "40ms",
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          <div style={{ height: 3, background: "linear-gradient(90deg,#6366f1,#8b5cf6)" }} />
          <div style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: "rgba(99,102,241,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrendingUp size={15} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                Revision Settings
              </h2>
            </div>

            {/* Toggle */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[
                { label: "By Percentage", val: false },
                { label: "By Monthly Amount", val: true },
              ].map(({ label, val }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setUseMonthlyAmount(val)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    border: `1.5px solid ${useMonthlyAmount === val ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                    background:
                      useMonthlyAmount === val ? "rgba(99,102,241,0.1)" : "var(--icon-btn-bg)",
                    color:
                      useMonthlyAmount === val ? "var(--brand-indigo)" : "var(--text-secondary)",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 16,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--text-muted)",
                    marginBottom: 6,
                  }}
                >
                  {salaryEnums.CURRENT_CTC}
                </div>
                <div
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: 800,
                    color: "var(--text-primary)",
                    padding: "8px 12px",
                    background: "var(--icon-btn-bg)",
                    borderRadius: 9,
                    border: "1px solid var(--glass-border)",
                  }}
                >
                  {previousSalary ? RupeesSymbol(previousSalary) : "—"}
                </div>
              </div>
              <BaseInput
                label={salaryEnums.REVISE_BY_PERCENTAGE}
                name="revise_percentage"
                type="text"
                placeholder={PlaceHolderFormat(salaryEnums.REVISE_BY_PERCENTAGE)}
                value={formik.values.revise_percentage}
                touched={formik.touched.revise_percentage}
                error={formik.errors.revise_percentage}
                handleBlur={formik.handleBlur}
                handleChange={(e) => {
                  if (e.target.value <= 100) handlePercentageChange(e);
                }}
                disabled={useMonthlyAmount}
              />
              <BaseInput
                label={salaryEnums.MONTHLY_AMOUNT}
                name="monthly_amount"
                type="number"
                placeholder={PlaceHolderFormat(salaryEnums.MONTHLY_AMOUNT)}
                value={formik.values.monthly_amount}
                touched={formik.touched.monthly_amount}
                error={formik.errors.monthly_amount}
                handleBlur={formik.handleBlur}
                handleChange={(e) => {
                  if (e.target.value <= formik.values.current_ctc * 2) handleBasicMonthlyChange(e);
                }}
                disabled={!useMonthlyAmount}
              />
              {pctChange !== 0 && (
                <div>
                  <div
                    style={{
                      fontSize: "0.6875rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--text-muted)",
                      marginBottom: 6,
                    }}
                  >
                    Change
                  </div>
                  <div
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: 800,
                      color: pctColor,
                      padding: "8px 12px",
                      background: `${pctColor}12`,
                      borderRadius: 9,
                      border: `1px solid ${pctColor}30`,
                    }}
                  >
                    {pctChange > 0 ? "+" : ""}
                    {Number(pctChange).toFixed(2)}%
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Salary breakdown */}
        <div
          className="animate-slide-up"
          style={{
            animationDelay: "80ms",
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          <div style={{ height: 3, background: "linear-gradient(90deg,#f59e0b,#f97316)" }} />
          <div style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: "rgba(245,158,11,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DollarSign size={15} strokeWidth={2} style={{ color: "#f59e0b" }} />
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                Salary Breakdown
              </h2>
            </div>

            {/* Column headers */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px 1fr 1fr",
                gap: 16,
                paddingBottom: 10,
                borderBottom: "2px solid var(--glass-border)",
                marginBottom: 4,
              }}
            >
              {[
                salaryEnums.COMPONENTS,
                salaryEnums.CALCULATION,
                salaryEnums.MONTHLY_AMOUNT,
                salaryEnums.YEARLY_AMOUNT,
              ].map((h) => (
                <span
                  key={h}
                  style={{
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--text-muted)",
                  }}
                >
                  {h}
                </span>
              ))}
            </div>
            <div
              style={{
                padding: "10px 0",
                color: "var(--brand-indigo)",
                fontSize: "0.8125rem",
                fontWeight: 700,
              }}
            >
              {salaryEnums.FIXED_EARNINGS}
            </div>

            {/* Basic pay row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px 1fr 1fr",
                gap: 16,
                padding: "10px 0",
                borderBottom: "1px solid var(--glass-border)",
                alignItems: "start",
              }}
            >
              <span style={{ fontSize: "0.875rem", color: "var(--text-primary)", paddingTop: 10 }}>
                {salaryEnums.SYSTEM_BASIC}
              </span>
              <span
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                  textAlign: "center",
                  paddingTop: 10,
                }}
              >
                {salaryEnums.FIXED}
              </span>
              <BaseInput
                name="basic_monthly"
                type="text"
                value={formik.values.basic_monthly}
                touched={formik.touched.basic_monthly}
                error={formik.errors.basic_monthly}
                handleBlur={formik.handleBlur}
                handleChange={handleBasicMonthlyChange}
                disabled
              />
              <BaseInput
                name="basic_yearly"
                type="text"
                value={formik.values.basic_yearly}
                touched={formik.touched.basic_yearly}
                error={formik.errors.basic_yearly}
                handleBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                disabled
              />
            </div>

            {/* Allowance row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px 1fr 1fr",
                gap: 16,
                padding: "10px 0",
                borderBottom: "1px solid var(--glass-border)",
                alignItems: "start",
              }}
            >
              <span style={{ fontSize: "0.875rem", color: "var(--text-primary)", paddingTop: 10 }}>
                {salaryEnums.SPECIAL_ALLOWANCE}
              </span>
              <span
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                  textAlign: "center",
                  paddingTop: 10,
                }}
              >
                {salaryEnums.FIXED}
              </span>
              <BaseInput
                name="allowance_monthly"
                type="text"
                value={formik.values.allowance_monthly}
                touched={formik.touched.allowance_monthly}
                error={formik.errors.allowance_monthly}
                handleBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                disabled
              />
              <BaseInput
                name="allowance_yearly"
                type="text"
                value={formik.values.allowance_yearly}
                touched={formik.touched.allowance_yearly}
                error={formik.errors.allowance_yearly}
                handleBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                disabled
              />
            </div>

            {/* CTC */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px 1fr 1fr",
                gap: 16,
                padding: "14px 0 0",
                marginTop: 4,
              }}
            >
              <span
                style={{ fontSize: "0.9375rem", fontWeight: 800, color: "var(--text-primary)" }}
              >
                {salaryEnums.CTC}
              </span>
              <span />
              <span
                style={{ fontSize: "0.9375rem", fontWeight: 800, color: "var(--brand-indigo)" }}
              >
                {RupeesSymbol(monthlyCTC)}
              </span>
              <span
                style={{ fontSize: "0.9375rem", fontWeight: 800, color: "var(--brand-indigo)" }}
              >
                {RupeesSymbol(yearlyCTC)}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingBottom: 8 }}>
          <BaseButton
            color="cancel"
            type="button"
            onClick={() => navigate(`/salaryStructure/${staffId}`)}
          >
            Cancel
          </BaseButton>
          <BaseButton color="primary" type="submit" loader={btnLoader} disabled={btnLoader}>
            {Submit}
          </BaseButton>
        </div>
      </form>
    </div>
  );
};

export default ReviseSalary;
